'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { stegaClean } from '@sanity/client/stega';

import { resolveI18nText } from '@/sanity/globals/I18n';
import { IBlogCategory, IBlogPostCard } from '@/sanity/lib/definitions';

const INITIAL_COUNT = 9;
const INCREMENT = 9;

export interface BlogCategoryPill {
  slug: string;
  label: string;
}

function normalize(text: string) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

function replaceParam(key: string, value: string) {
  const params = new URLSearchParams(window.location.search);
  if (value) params.set(key, value);
  else params.delete(key);
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
}

export function useBlogGrid(
  posts: IBlogPostCard[],
  categories: IBlogCategory[],
  language: string,
) {
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setActiveCategory(params.get('category') ?? '');
    setSearch(params.get('search') ?? '');
    return () => {
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
    };
  }, []);

  const pills = useMemo<BlogCategoryPill[]>(() => {
    const used = new Set(posts.map((p) => p.category?._id).filter(Boolean));
    return categories
      .filter((c) => used.has(c._id) && stegaClean(c.slug))
      .map((c) => ({
        slug: stegaClean(c.slug) as string,
        label: resolveI18nText(c.name, c.nameI18n, language),
      }));
  }, [posts, categories, language]);

  const category = pills.some((p) => p.slug === activeCategory) ? activeCategory : '';

  const searchable = useMemo(
    () =>
      posts.map((post) => ({
        post,
        category: stegaClean(post.category?.slug) ?? '',
        text: normalize(stegaClean(`${post.title ?? ''} ${post.excerpt ?? ''}`)),
      })),
    [posts],
  );

  const filtered = useMemo(() => {
    const query = normalize(search.trim());
    return searchable
      .filter((item) => !category || item.category === category)
      .filter((item) => !query || item.text.includes(query))
      .map((item) => item.post);
  }, [searchable, category, search]);

  const visible = filtered.slice(0, visibleCount);
  const rest = filtered.slice(visibleCount);
  const hasMore = rest.length > 0;

  function handleCategory(slug: string) {
    setActiveCategory(slug);
    setVisibleCount(INITIAL_COUNT);
    replaceParam('category', slug);
  }

  function handleSearch(value: string) {
    setSearch(value);
    setVisibleCount(INITIAL_COUNT);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => replaceParam('search', value.trim()), 200);
  }

  function loadMore() {
    setVisibleCount((c) => c + INCREMENT);
  }

  function scrollTabs(dir: 'left' | 'right') {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({
      left: (dir === 'right' ? 1 : -1) * el.clientWidth * 0.6,
      behavior: 'smooth',
    });
  }

  return {
    pills,
    visible,
    rest,
    hasMore,
    activeCategory: category,
    search,
    tabsRef,
    handleCategory,
    handleSearch,
    loadMore,
    scrollTabs,
  };
}
